import { useI18n } from '../../i18n/I18nProvider'
import { useTheme } from '../../hooks/useTheme'
import { LICENSE_URL, REFERENCE_URL, REPO_URL, SPEC_URL } from '../../config'
import { IconButton, cx } from '../ui/primitives'
import { GithubIcon, LockIcon, MoonIcon, SunIcon } from '../ui/icons'

export function TopBar() {
  const { t, locale, toggleLocale } = useI18n()
  const { theme, toggleTheme } = useTheme()

  return (
    // Fundo quase opaco por padrão; só afina onde backdrop-filter existe de
    // fato, senão o conteúdo da página atravessa a barra.
    <header className="sticky top-0 z-30 border-b border-border bg-bg/95 supports-[backdrop-filter]:bg-bg/75 supports-[backdrop-filter]:backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-5xl items-center gap-2 px-4">
        <a href="#gerador" className="mr-auto flex min-w-0 items-center gap-2.5">
          <span className="accent-gradient text-on-accent flex size-9 shrink-0 items-center justify-center rounded-xl shadow-card">
            <LockIcon className="size-5" />
          </span>
          <span className="min-w-0">
            <span className="block truncate font-mono text-sm font-semibold text-fg">
              {t.brand.name}
            </span>
            <span className="hidden truncate text-xs text-fg-subtle sm:block">
              {t.brand.tagline}
            </span>
          </span>
        </a>

        <button
          type="button"
          onClick={toggleLocale}
          aria-label={t.topbar.lang}
          title={t.topbar.lang}
          className="h-11 rounded-xl px-3 font-mono text-xs font-semibold text-fg-muted uppercase transition hover:bg-surface-2 hover:text-fg"
        >
          {locale === 'pt' ? 'PT' : 'EN'}
        </button>

        <IconButton label={t.topbar.theme} onClick={toggleTheme}>
          {theme === 'dark' ? <SunIcon className="size-5" /> : <MoonIcon className="size-5" />}
        </IconButton>

        <a
          href={REPO_URL}
          target="_blank"
          rel="noreferrer noopener"
          aria-label={t.topbar.source}
          title={t.topbar.source}
          className="inline-flex size-11 items-center justify-center rounded-xl text-fg-muted transition hover:bg-surface-2 hover:text-fg"
        >
          <GithubIcon className="size-5" />
        </a>
      </div>
    </header>
  )
}

export function Hero() {
  const { t } = useI18n()
  return (
    <section className="pt-10 pb-8 sm:pt-16 sm:pb-12">
      <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-surface-2/70 px-3 py-1.5 text-xs font-medium text-fg-muted">
        <span className="size-1.5 rounded-full bg-ok" />
        {t.hero.badge}
      </p>
      <h1 className="text-4xl leading-[1.08] font-semibold tracking-tight text-balance text-fg sm:text-5xl lg:text-6xl">
        {t.hero.titleLead} <span className="text-gradient">{t.hero.titleAccent}</span>{' '}
        {t.hero.titleTail}
      </h1>
      <p className="mt-4 max-w-2xl text-base leading-relaxed text-pretty text-fg-muted sm:text-lg">
        {t.hero.subtitle}
      </p>

      <dl className="mt-8 grid gap-3 sm:grid-cols-3">
        {t.hero.facts.map((fact) => (
          <div key={fact.value} className="surface-card p-4">
            <dt className="text-gradient font-mono text-xl font-semibold">{fact.value}</dt>
            <dd className="mt-1 text-xs leading-relaxed text-fg-muted">{fact.label}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

export function Footer() {
  const { t } = useI18n()
  const links = [
    { href: SPEC_URL, label: t.footer.spec },
    { href: REFERENCE_URL, label: t.footer.reference },
    { href: REPO_URL, label: t.footer.code },
    { href: LICENSE_URL, label: t.footer.license },
  ]

  return (
    <footer className="border-t border-border py-10">
      <div className="flex flex-col gap-4">
        <nav className="flex flex-wrap gap-x-5 gap-y-2">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noreferrer noopener"
              className={cx(
                'text-sm text-fg-muted underline decoration-border underline-offset-4',
                'transition hover:text-fg hover:decoration-accent',
              )}
            >
              {link.label}
            </a>
          ))}
        </nav>
        <p className="max-w-3xl text-xs leading-relaxed text-fg-subtle">
          {t.footer.disclaimer}
        </p>
        <p className="flex items-center gap-2 font-mono text-xs text-fg-subtle">
          <span className="size-1.5 rounded-full bg-ok" />
          {t.footer.offline} · {t.brand.expansion} · dpgen:v1
        </p>
      </div>
    </footer>
  )
}
